import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { Section } from '../../../components/AdminContentWrapper';
import CreditsEngineService from '../../../../../services/CreditsEngineService';

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' },
  '& .MuiFormHelperText-root': { color: 'var(--wb-muted-70)' }
};

const KIND_LABELS = { daily_threshold: 'Umbral diario', spike: 'Pico' };

const toForm = (config) => ({
  enabled: config?.enabled ?? true,
  daily_usd_threshold: String(config?.daily_usd_threshold ?? ''),
  spike_factor: String(config?.spike_factor ?? ''),
  spike_min_usd: String(config?.spike_min_usd ?? ''),
  overrides: Object.entries(config?.tenant_overrides || {}).map(([accountId, o]) => ({
    accountId,
    threshold: String(o.daily_usd_threshold ?? '')
  }))
});

/**
 * Alertas de costo por tenant (UsageControlFunction, credits-engine): el
 * chequeo horario compara el costo de HOY contra el umbral diario, el diario
 * compara AYER contra el promedio de los 7 días previos. Cada alerta se
 * notifica una sola vez por SNS (tema UsageAlertsTopic) y queda listada acá.
 */
const UsageAlertsSection = ({ range }) => {
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState(null);
  const [savedBy, setSavedBy] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [alertList, config] = await Promise.all([
        CreditsEngineService.getInfrastructureAlerts(range),
        CreditsEngineService.getInfrastructureAlertsConfig()
      ]);
      setAlerts(alertList);
      setForm(toForm(config));
      setSavedBy(config?.updated_at ? `${config.updated_by || '—'} · ${config.updated_at}` : null);
    } catch (err) {
      setError(err.message || 'No fue posible obtener las alertas.');
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setOverride = (index, field) => (e) =>
    setForm((f) => ({
      ...f,
      overrides: f.overrides.map((o, i) => (i === index ? { ...o, [field]: e.target.value } : o))
    }));

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      const tenantOverrides = {};
      form.overrides
        .filter((o) => o.accountId.trim())
        .forEach((o) => {
          tenantOverrides[o.accountId.trim()] = { daily_usd_threshold: Number(o.threshold) };
        });
      const saved = await CreditsEngineService.updateInfrastructureAlertsConfig({
        enabled: form.enabled,
        daily_usd_threshold: Number(form.daily_usd_threshold),
        spike_factor: Number(form.spike_factor),
        spike_min_usd: Number(form.spike_min_usd),
        tenant_overrides: tenantOverrides
      });
      setForm(toForm(saved));
      setSavedBy(`${saved.updated_by || '—'} · ${saved.updated_at}`);
      setToast('Configuración de alertas guardada.');
    } catch (err) {
      setError(err.message || 'No fue posible guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Section
        title="Alertas de costo por tenant"
        subtitle="Cada hora se revisa el costo estimado de hoy contra el umbral diario; cada día, el de ayer contra el promedio de los 7 días previos. Cada alerta se notifica una sola vez (tema SNS whisperbi-usage-alerts)."
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {alerts.length === 0 ? (
          <Typography sx={{ color: 'var(--wb-muted-70)' }}>Sin alertas en el período seleccionado.</Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Día (UTC)</TableCell>
                  <TableCell sx={headSx}>Tipo</TableCell>
                  <TableCell sx={headSx}>Tenant</TableCell>
                  <TableCell sx={headSx}>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((a) => (
                  <TableRow key={`${a.day}#${a.account_id}#${a.kind}`}>
                    <TableCell sx={cellSx}>{a.day}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip size="small" label={KIND_LABELS[a.kind] || a.kind}
                        sx={{ backgroundColor: a.kind === 'spike' ? 'var(--wb-danger-16)' : 'var(--wb-x-bg-fbbf2429, rgba(251,191,36,0.16))',
                          color: a.kind === 'spike' ? 'var(--wb-danger)' : 'var(--wb-warning)' }} />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.account_id}</TableCell>
                    <TableCell sx={{ ...cellSx, fontSize: '0.85rem' }}>{a.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {form && (
        <Section title="Configuración de alertas" subtitle={savedBy ? `Última modificación: ${savedBy}` : 'Valores por defecto (sin modificar).'}>
          <FormControlLabel
            sx={{ color: 'var(--wb-text)', mb: 2 }}
            control={<Switch checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />}
            label="Alertas activas"
          />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="number" label="Umbral diario por tenant (USD)"
                value={form.daily_usd_threshold} onChange={setField('daily_usd_threshold')} sx={darkTextFieldStyle}
                helperText="Costo estimado de un tenant en un día que dispara la alerta." />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="number" label="Factor de pico"
                value={form.spike_factor} onChange={setField('spike_factor')} sx={darkTextFieldStyle}
                helperText="Ayer ≥ factor × promedio de los 7 días previos." />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="number" label="Mínimo para pico (USD)"
                value={form.spike_min_usd} onChange={setField('spike_min_usd')} sx={darkTextFieldStyle}
                helperText="Por debajo de este monto no se alerta (evita ruido)." />
            </Grid>
          </Grid>

          <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600, mb: 1 }}>Umbral diario por tenant (excepciones)</Typography>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {form.overrides.map((o, index) => (
              <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField size="small" label="account_id" value={o.accountId} onChange={setOverride(index, 'accountId')}
                  sx={{ flex: 2, ...darkTextFieldStyle }} />
                <TextField size="small" type="number" label="Umbral (USD)" value={o.threshold}
                  onChange={setOverride(index, 'threshold')} sx={{ flex: 1, ...darkTextFieldStyle }} />
                <IconButton aria-label="Quitar excepción" sx={{ color: 'var(--wb-danger)' }}
                  onClick={() => setForm((f) => ({ ...f, overrides: f.overrides.filter((_, i) => i !== index) }))}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Button startIcon={<AddIcon />} sx={{ color: 'var(--wb-blue)' }}
              onClick={() => setForm((f) => ({ ...f, overrides: [...f.overrides, { accountId: '', threshold: '' }] }))}>
              Agregar excepción
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}
              sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
              Guardar
            </Button>
          </Box>
        </Section>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} message={toast} />
    </>
  );
};

export default UsageAlertsSection;
